import { useToggleStore } from '../../../hooks/useTogger';
import EditIcon from '@mui/icons-material/Edit';
import MenuIcon from '@mui/icons-material/Menu';
import { useWindowHeight } from '../../../hooks/useWindowHeight';
import { IListChat } from '../../../api/chat';
import InfiniteScroll from 'react-infinite-scroll-component';
import { ProfileSuccessResponse } from '../../../api/authencation';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  InputAdornment,
  TextField,
} from '@mui/material';
import SettingsIcon from '@mui/icons-material/Settings';
import { useState, useCallback, useEffect } from 'react';
import SettingsDialog from './SettingsDialog';
import { ASSETS } from '../../../const/assets';
import { debounce } from 'lodash';

interface MenuBarProps {
  hasMore: boolean;
  isLoading: boolean;
  data: IListChat[] | undefined;
  fetchNextPage: () => void;
  profile: ProfileSuccessResponse | undefined;
  handleChooseConversation: (conversation: IListChat) => void;
  newConversation: () => void;
  idConversationCurrent: string | null;
  handleUpdateSearchQuery: (query: string) => void;
  searchQuery: string;
}

function MenuBar({
  hasMore,
  isLoading,
  data,
  fetchNextPage,
  profile,
  handleChooseConversation,
  newConversation,
  idConversationCurrent,
  handleUpdateSearchQuery,
}: MenuBarProps) {
  const { width, toggle } = useToggleStore();
  const windowHeight = useWindowHeight();
  const [searchValue, setSearchValue] = useState('');

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('isAuthenticated');

    window.location.href = '/main-login';
  };

  const [openLogoutDialog, setOpenLogoutDialog] = useState(false);

  // Create a debounced function with useCallback to prevent recreation on each render
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedSearch = useCallback(
    debounce((query: string) => {
      handleUpdateSearchQuery(query);
    }, 500),
    [handleUpdateSearchQuery]
  );

  // Handle input change
  const handleChangeSearchQuery = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchValue(value);
    debouncedSearch(value);
  };

  // Clean up the debounce on unmount
  useEffect(() => {
    return () => {
      debouncedSearch.cancel();
    };
  }, [debouncedSearch]);

  // Tính toán tổng số items từ tất cả các pages
  const items = data || [];

  // Group conversations by date
  const groupedItems = items.reduce((groups, chat) => {
    const date = new Date(chat.createdAt);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    let formattedDate;

    if (date.toDateString() === today.toDateString()) {
      formattedDate = 'Today';
    } else if (date.toDateString() === yesterday.toDateString()) {
      formattedDate = 'Yesterday';
    } else {
      formattedDate = date.toLocaleDateString('vi-VN', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      });
    }

    if (!groups[formattedDate]) {
      groups[formattedDate] = [];
    }

    groups[formattedDate].push(chat);
    return groups;
  }, {} as Record<string, IListChat[]>);

  const [openSettingsDialog, setOpenSettingsDialog] = useState(false);

  return (
    <Box
      style={{
        width: `${width}px`,
        height: `${windowHeight}px`,
        transition: 'width 0.3s ease-in-out',
        transform: `translateX(${width === 0 ? '-100%' : '0'})`,
        opacity: width === 0 ? 0 : 1,
      }}
      className={`bg-[#FAFAFA] h-full border-r border-gray-200 md:relative absolute z-10 ${
        width === 0 ? 'hidden' : ''
      }`}
    >
      <div className="border-[#ECECEC] border-b">
        <div className=" px-6 h-16 flex items-center justify-between">
          <div
            onClick={() => {
              toggle();
            }}
          >
            <MenuIcon className="cursor-pointer text-gray-600" />
          </div>
          <EditIcon
            className="cursor-pointer text-gray-600"
            onClick={newConversation}
          />
        </div>
        <div className="px-6 pb-3">
          <TextField
            fullWidth
            size="small"
            placeholder="Search"
            value={searchValue}
            onChange={handleChangeSearchQuery}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: '8px',
                backgroundColor: '#FFFFFF',
                '& fieldset': {
                  borderColor: 'white',
                },
                '&:hover fieldset': {
                  borderColor: 'white',
                },
                '&.Mui-focused fieldset': {
                  borderColor: 'white',
                },
              },
            }}
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <img src={ASSETS.icSearch} alt="search" />
                  </InputAdornment>
                ),
                className: 'rounded-lg text-sm',
              },
            }}
          />
        </div>
      </div>
      <div className="h-[calc(100%-11.35rem)]">
        {isLoading ? (
          <div className="flex justify-center items-center h-full">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
          </div>
        ) : (
          <InfiniteScroll
            dataLength={items.length}
            next={fetchNextPage}
            hasMore={hasMore}
            loader={
              <div className="flex justify-center py-4">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-gray-900"></div>
              </div>
            }
            scrollableTarget="scrollableDiv"
            className="px-6 py-4"
            height={`calc(${windowHeight > 0 ? windowHeight - 180 : 0}px)`}
          >
            {Object.entries(groupedItems).map(([date, chats]) => (
              <div key={date} className="mb-4">
                <div className="text-xs text-gray-500 mb-2 font-medium">
                  {date}
                </div>
                {chats.map((chat) => (
                  <div
                    key={chat.id}
                    className={`cursor-pointer py-2 px-4 rounded-lg
                        ${
                          chat.id.toString() ===
                          idConversationCurrent?.toString()
                            ? 'bg-gray-100'
                            : ''
                        }`}
                    onClick={() => {
                      handleChooseConversation(chat);
                    }}
                  >
                    <div className="relative z-10 flex items-center justify-between">
                      <div className="flex items-center space-x-3">
                        <div className="flex flex-col flex-1">
                          <span className="text-zinc-800 font-medium text-sm">
                            {chat.title || 'Untitled Chat'}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ))}
          </InfiniteScroll>
        )}
      </div>
      <div className="h-[4rem] px-6 flex items-center justify-between border-t border-gray-200 relative bg-white shadow-[0_-1px_3px_0_rgba(0,0,0,0.1)] z-10">
        <div className="flex items-center space-x-3 flex-1">
          <div className="h-8 w-8 rounded-lg bg-gradient-to-br from-violet-500 to-fuchsia-500 flex items-center justify-center">
            <span className="text-white text-sm font-medium">
              {profile?.firstName?.charAt(0).toUpperCase() || ''}
              {profile?.lastName?.charAt(0).toUpperCase() || ''}
            </span>
          </div>
          <div className="flex space-x-3 flex-col">
            <span className="text-zinc-800 font-medium text-sm">
              {profile?.firstName || ''} {profile?.lastName || ''}
            </span>
          </div>
        </div>
        <div className="flex items-center justify-center gap-2">
          <Box
            className="flex items-center gap-3 cursor-pointer md:hidden"
            sx={{
              justifyContent: { xs: 'center', md: 'flex-start' },
            }}
            onClick={() => {
              setOpenLogoutDialog(true);
            }}
          >
            <img src={ASSETS.icLogout} alt="logout" />
          </Box>
          <Dialog
            open={openLogoutDialog}
            onClose={() => setOpenLogoutDialog(false)}
            slotProps={{
              paper: {
                className: '!rounded-2xl overflow-hidden',
              },
            }}
          >
            <DialogTitle className="text-black font-bold flex items-center justify-center !pb-2">
              Logout
            </DialogTitle>
            <DialogContent className="mt-4">
              <DialogContentText className="text-black px-5">
                Are your 100% sure you want to logout
              </DialogContentText>
            </DialogContent>
            <DialogActions className="p-4 flex gap-2 !justify-center mb-3">
              <Button
                onClick={() => setOpenLogoutDialog(false)}
                className="text-zinc-600 hover:text-zinc-800 normal-case px-4"
                variant="text"
                sx={{
                  textTransform: 'none',
                  color: 'black',
                  borderRadius: '100px',
                  border: '1px solid #B9BCC4',
                  px: 4,
                }}
              >
                Cancel
              </Button>
              <Button
                onClick={() => {
                  setOpenLogoutDialog(false);
                  handleLogout();
                }}
                variant="contained"
                sx={{
                  backgroundColor: '#E64646',
                  color: 'white',
                  borderRadius: '100px',
                  border: '1px solid #E64646',
                  px: 3,
                  textTransform: 'none',
                  '&:hover': {
                    backgroundColor: '#E64646',
                  },
                }}
              >
                Confirm Logout
              </Button>
            </DialogActions>
          </Dialog>
          <SettingsIcon
            className="cursor-pointer text-gray-600"
            onClick={() => setOpenSettingsDialog(true)}
          />
          <SettingsDialog
            open={openSettingsDialog}
            onClose={() => setOpenSettingsDialog(false)}
            profile={profile}
          />
        </div>
      </div>
    </Box>
  );
}

export default MenuBar;
